import type { TraceEvent } from "@/types/dashboard";
import type { Activity, Evaluation } from "./types";

// Helper function to format request content nicely
export function formatRequestContent(content: string): string {
  try {
    // Try to parse as JSON first
    const parsed = JSON.parse(content);
    if (parsed.request && typeof parsed.request === 'string') {
      content = parsed.request;
    }
  } catch {
    // Not JSON, use as-is
  }

  // Check if content contains structured agent request format
  if (content.includes('Question:') && content.includes('Discovered Tables:')) {
    const lines: string[] = [];

    // Extract question
    const questionMatch = content.match(/Question:\s*([^\n]+)/);
    if (questionMatch) {
      lines.push('📋 Question');
      lines.push(questionMatch[1]);
      lines.push('');
    }

    // Extract discovered tables
    const tablesSection = content.match(/Discovered Tables:\s*([\s\S]*?)(?=JOIN KEYS:|$)/);
    if (tablesSection) {
      lines.push('📊 Discovered Tables');
      lines.push('');

      // Parse each table entry (numbered)
      const tableMatches = tablesSection[1].matchAll(/(\d+)\.\s+([^:]+):\s+([^\n]+)\n\s+-\s+Type:\s+([^\n]+)\n\s+-\s+Description:\s+([^\n]+)\n\s+-\s+Key columns:\s+([^\n]+)\n\s+-\s+Reason:\s+([^\n]+)/g);

      for (const match of tableMatches) {
        const [, num, tableName, fullPath, type, desc, keyCols, reason] = match;
        lines.push(`${num}. ${tableName.trim()}`);
        lines.push(`   📍 ${fullPath.trim()}`);
        lines.push(`   🏷️  Type: ${type.trim()}`);
        lines.push(`   📝 ${desc.trim()}`);
        lines.push(`   🔑 Columns: ${keyCols.trim()}`);
        lines.push(`   💡 ${reason.trim()}`);
        lines.push('');
      }
    }

    // Extract join keys
    const joinKeysMatch = content.match(/JOIN KEYS:\s*([\s\S]*)/);
    if (joinKeysMatch) {
      lines.push('🔗 Join Keys');
      const joinKeys = joinKeysMatch[1].trim().split('\n-').map(k => k.trim()).filter(k => k);
      joinKeys.forEach(key => {
        lines.push(`   • ${key.replace(/^-\s*/, '')}`);
      });
    }

    return lines.join('\n');
  }

  // Return as-is if no special formatting needed
  return content;
}
export function formatTimestamp(timestamp: string) {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  const ms = date.getMilliseconds().toString().padStart(3, '0');
  const time = date.toLocaleTimeString('nl-NL', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
  return `${time}.${ms}`;
}

export function getScoreCategory(score: number) {
  if (score >= 4.5) return "Uitstekend";
  if (score >= 3.5) return "Goed";
  if (score >= 2.5) return "Matig";
  return "Onvoldoende";
}

/** Answer may be JSON (optionally in a ```json block) with `answer` and `sql` fields. */
export function parseAnswer(answer?: string, sql?: string): { plainAnswer?: string; finalSql?: string } {
  let parsedAnswer = null;
  let plainAnswer = answer;
  if (answer) {
    try {
      let jsonText = answer;
      const jsonBlockMatch = answer.match(/```json\s*\n([\s\S]*?)\n```/);
      if (jsonBlockMatch) jsonText = jsonBlockMatch[1];
      parsedAnswer = JSON.parse(jsonText);
      if (parsedAnswer && typeof parsedAnswer === 'object') {
        plainAnswer = parsedAnswer.answer || answer;
      }
    } catch { /* not JSON: keep plain answer */ }
  }
  return { plainAnswer, finalSql: parsedAnswer?.sql || sql };
}

// Extract evaluation from traces if not provided
export function resolveEvaluation(evaluation: Evaluation | undefined, traces: TraceEvent[]): Evaluation | null {
  if (evaluation) return evaluation;
  const evalEvent = traces.find(t => t.data?.evaluation_complete);
  if (evalEvent?.data) {
    return {
      score: evalEvent.data.accuracy_score,
      confidence: evalEvent.data.confidence,
      critique: evalEvent.data.critique,
      reasoning: evalEvent.data.reasoning,
    };
  }
  return null;
}

/** Data for the "Resultaat" tab: trace data first (cleanest source), then props. */
export function extractResults(traces: TraceEvent[], sql: string | undefined, answer: string | undefined, evaluationData: Evaluation | null) {
  // 1. Find SQL from traces (cleanest source)
  const sqlTraceEvent = traces.find(t => t.type === 'sql_extraction' && t.sql);
  const traceSql = sqlTraceEvent?.sql;

  // 2. Find answer from traces (agent_response with extracted_text)
  const answerTraceEvent = traces.find(t => t.type === 'agent_response' && t.extracted_text);
  const traceAnswer = answerTraceEvent?.extracted_text;

  // 3. Find evaluation from traces
  const evalTraceEvent = traces.find(t => t.type === 'accuracy_evaluation' && t.score != null);
  const traceEvaluation: Evaluation | null = evalTraceEvent ? {
    score: evalTraceEvent.score as number,
    confidence: evalTraceEvent.confidence?.toString(),
    reasoning: evalTraceEvent.reasoning,
    critique: evalTraceEvent.critique
  } : null;

  // Use trace data first, then fall back to props
  const rawAnswer = traceAnswer || answer;

  // Extract JUST the answer text (remove SQL blocks and headers)
  let cleanAnswer = rawAnswer;
  if (cleanAnswer) {
    // Remove "SQL QUERY:" section with code block
    cleanAnswer = cleanAnswer.replace(/SQL QUERY:\s*```sql[\s\S]*?```\s*/gi, '');
    // Remove standalone ```sql blocks
    cleanAnswer = cleanAnswer.replace(/```sql[\s\S]*?```\s*/gi, '');
    // Remove "ANSWER:" header if present
    cleanAnswer = cleanAnswer.replace(/^ANSWER:\s*/im, '');
    // Clean up extra whitespace
    cleanAnswer = cleanAnswer.trim();
  }

  return {
    cleanAnswer,
    finalSql: traceSql || sql,
    finalEvaluation: traceEvaluation || evaluationData,
    // Footer flags (same rules as before: raw presence, not cleaned)
    hasSql: Boolean(traceSql || sql),
    hasAnswer: Boolean(traceAnswer || answer),
    hasEval: Boolean(evalTraceEvent || evaluationData),
  };
}

/** Plain-text dump of the whole task (results, evaluation, workflow, raw events) for the clipboard. */
export function buildCondensedTrace({
  questionId,
  question,
  answer,
  sql,
  evaluationData,
  activities,
  traces,
}: {
  questionId: string;
  question: string;
  answer?: string;
  sql?: string;
  evaluationData: Evaluation | null;
  activities: Activity[];
  traces: TraceEvent[];
}): string {
  const lines: string[] = [];
  const rule = '═══════════════════════════════════════════════════════════════';

  // Header
  lines.push(rule);
  lines.push(`TASK: ${questionId}`);
  lines.push(rule);
  lines.push('');
  lines.push('QUESTION:');
  lines.push(question);
  lines.push('');

  const { plainAnswer, finalSql } = parseAnswer(answer, sql);

  // RESULTS - SQL & ANSWER
  lines.push(rule);
  lines.push('RESULTS');
  lines.push(rule);
  lines.push('');

  if (finalSql) {
    lines.push('--- SQL QUERY ---');
    lines.push(finalSql);
    lines.push('');
  }

  if (plainAnswer) {
    lines.push('--- ANSWER ---');
    lines.push(plainAnswer);
    lines.push('');
  }

  // EVALUATION
  if (evaluationData) {
    lines.push(rule);
    lines.push('EVALUATION');
    lines.push(rule);
    lines.push('');
    lines.push(`Score: ${evaluationData.score?.toFixed(1) || 'N/A'}/5.0 (${getScoreCategory(evaluationData.score || 0)})`);
    if (evaluationData.confidence) {
      lines.push(`Confidence: ${evaluationData.confidence}`);
    }
    if (evaluationData.reasoning) {
      lines.push('');
      lines.push('--- Reasoning ---');
      lines.push(evaluationData.reasoning);
    }
    if (evaluationData.critique) {
      lines.push('');
      lines.push('--- Critique ---');
      lines.push(evaluationData.critique);
    }
    lines.push('');
  }

  // TRACE - WORKFLOW ACTIVITIES
  const nestedActivities = activities.filter(a => a.metadata?.isNested);
  if (nestedActivities.length > 0) {
    lines.push(rule);
    lines.push('TRACE (WORKFLOW)');
    lines.push(rule);

    nestedActivities.forEach((a, idx) => {
      lines.push('');
      lines.push(`───────────────────────────────────────────────────────────────`);
      lines.push(`[${idx + 1}] ${a.title}`);
      lines.push(`───────────────────────────────────────────────────────────────`);

      if (a.request) {
        const formatted = formatRequestContent(a.request);
        lines.push('');
        lines.push('REQUEST:');
        lines.push(formatted);
      }

      if (a.result) {
        lines.push('');
        lines.push('RESULT:');
        const resultStr = typeof a.result === 'string' ? a.result : JSON.stringify(a.result, null, 2);
        lines.push(resultStr);
      }

      if (a.state) {
        lines.push('');
        lines.push('STATE:');
        try {
          const stateObj = typeof a.state === 'string' ? JSON.parse(a.state) : a.state;
          if (stateObj && typeof stateObj === 'object') {
            Object.entries(stateObj).forEach(([key, value]) => {
              lines.push('');
              lines.push(`▼ ${key}`);
              const valueStr = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
              const indentedValue = valueStr.split('\n').map(line => '  ' + line).join('\n');
              lines.push(indentedValue);
            });
          } else {
            const stateStr = typeof a.state === 'string' ? a.state : JSON.stringify(a.state, null, 2);
            lines.push(stateStr);
          }
        } catch {
          const stateStr = typeof a.state === 'string' ? a.state : JSON.stringify(a.state, null, 2);
          lines.push(stateStr);
        }
      }

      if (a.error) {
        lines.push('');
        lines.push('ERROR:');
        lines.push(a.error);
      }
    });
    lines.push('');
  }

  // RAW TRACE EVENTS (for debugging)
  if (traces.length > 0) {
    lines.push(rule);
    lines.push('RAW TRACE EVENTS');
    lines.push(rule);
    lines.push('');
    lines.push(JSON.stringify(traces, null, 2));
  }

  return lines.join('\n');
}
